import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { TonConnectUIProvider, THEME } from "@tonconnect/ui-react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Loader2, ChevronLeft, Clock, ExternalLink, QrCode, CheckCircle2, RefreshCcw, ShieldAlert, Gift, Package } from "lucide-react";
import { Shell } from "../components/Shell";
import { AuthForms } from "../components/AccountSheet";
import { OrderTimeline } from "../components/OrderTimeline";
import { TonPayButton } from "../components/TonPayButton";
import { WalletSteps, WriteToKimberly } from "../components/PayHelp";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { useFan, errMsg } from "../context/FanContext";
import { API } from "../lib/scroll";
import { METHOD_META, STATUS_META, TONE, PAYABLE, copyText, useCountdown, fmtDateTime, linkify } from "../lib/pay";
import { fmtEur } from "../config/site";

const MANIFEST = `${API}/tonconnect-manifest.json`;
const field = "h-12 rounded-[14px] border-[rgba(242,215,208,0.16)] bg-[#0b0b0d] text-[#f5f2ee] placeholder:text-[#6f6a64]";

const Row = ({ label, value, copy, mono = true, testId, big }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-[rgba(0,175,240,0.14)] bg-[#0b0b0d] px-4 py-3">
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a847d]">{label}</div>
      <div data-testid={testId} className={`${mono ? "font-mono" : ""} ${big ? "text-xl text-[color:var(--gold-light)]" : "text-sm text-[#f5f2ee]"} break-all`}>{value}</div>
    </div>
    {copy && <button data-testid={`${testId}-copy`} onClick={() => copyText(value, `${label} copiato`)} className="btn-ghost-lux flex h-10 w-10 shrink-0 items-center justify-center rounded-full" aria-label={`Copia ${label}`}><Copy className="h-4 w-4" /></button>}
  </div>
);

const QRBox = ({ value }) => (
  <div className="mx-auto w-fit rounded-2xl border border-[rgba(0,175,240,0.35)] bg-[#f5f2ee] p-3" data-testid="order-qr">
    <QRCodeSVG value={value} size={196} bgColor="#f5f2ee" fgColor="#0b0b0d" level="M" />
  </div>
);

function OrderInner() {
  const { code } = useParams();
  const { fan, token, authHeaders, refresh } = useFan();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);
  const [config, setConfig] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claim, setClaim] = useState({ txid: "", reference: "" });
  const [busy, setBusy] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const tick = useRef(0);
  const headers = useMemo(() => authHeaders(), [authHeaders]);

  const load = useCallback(async (check = false) => {
    if (!token) return;
    try {
      const url = `${API}/orders/${code}${check ? "/check" : ""}`;
      const { data } = check ? await axios.post(url, {}, { headers }) : await axios.get(url, { headers });
      setOrder((prev) => {
        if (prev && prev.status !== data.status && (data.status === "confermato" || data.status === "consegnato")) { toast.success(STATUS_META[data.status].label); refresh(); }
        return data;
      });
      setErr(null);
    } catch (e) {
      setErr(errMsg(e, "Ordine non trovato"));
    }
  }, [code, token, headers, refresh]);

  useEffect(() => { load(); axios.get(`${API}/pay/config`).then((r) => setConfig(r.data)).catch(() => {}); }, [load]);
  useEffect(() => {
    if (!order || !PAYABLE.includes(order.status)) return undefined;
    const t = setInterval(() => { tick.current += 1; load(order.payment.method !== "sepa" && tick.current % 3 === 0); }, 8000);
    return () => clearInterval(t);
  }, [order, load]);

  const cd = useCountdown(order?.expires_at || new Date().toISOString(), order?.created_at);

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-5 pb-32 pt-28" data-testid="order-auth-required">
        <h1 className="font-display text-3xl italic text-[#f5f2ee]">Accedi per vedere l'ordine {code}</h1>
        <div className="lux-card mt-6 p-6"><AuthForms compact /></div>
      </div>
    );
  }
  if (err) return <div className="mx-auto max-w-md px-5 pb-32 pt-28 text-center" data-testid="order-error"><p className="font-display text-2xl italic text-[#f5f2ee]">{err}</p><Link to="/" className="btn-gold mt-6 inline-block rounded-xl px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em]">Torna al sito</Link></div>;
  if (!order) return <div className="flex justify-center pt-40"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--gold)]" /></div>;

  const p = order.payment;
  const meta = STATUS_META[order.status];
  const payable = PAYABLE.includes(order.status);
  const available = (config?.methods || []).filter((m) => m.available);

  const submitClaim = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/orders/${code}/paid`, { txid: claim.txid || null, reference: claim.reference || null }, { headers });
      setOrder(data); setClaimOpen(false);
      toast.success(data.status === "confermato" ? "Pagamento trovato e confermato!" : "Segnalazione ricevuta: controllo e confermo io");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const changeMethod = async (m) => {
    if (m === p.method) return;
    setBusy(true);
    try { const { data } = await axios.patch(`${API}/orders/${code}/method`, { method: m }, { headers }); setOrder(data); toast.success(`Metodo: ${METHOD_META[m].short}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const manualCheck = async () => { setBusy(true); await load(true); setBusy(false); toast("Controllo fatto"); };

  return (
    <section className="mx-auto max-w-6xl px-5 pb-36 pt-24 sm:px-8 sm:pt-28" data-testid="order-page">
      <button data-testid="order-back-link" onClick={() => window.__navigate?.("/")} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-[#8a847d] hover:text-[#f5f2ee]"><ChevronLeft className="h-3.5 w-3.5" /> Torna al sito</button>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--blush)]">Ordine</span>
          <h1 className="font-display text-3xl italic text-[#f5f2ee] sm:text-4xl" data-testid="order-item-name">{order.item.name}</h1>
        </div>
        <span data-testid="order-status-badge" className={`rounded-full border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] ${TONE[meta.tone]}`}>{meta.label}</span>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px] lg:items-start">
        <div className="space-y-5">
          {/* Codice ordine */}
          <div className="lux-card flex items-center justify-between gap-4 p-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a847d]">Codice ordine</div>
              <div className="font-mono text-3xl tracking-[0.12em] text-[color:var(--gold-light)] sm:text-4xl" data-testid="order-code">{order.code}</div>
            </div>
            <button data-testid="order-code-copy" onClick={() => copyText(order.code, "Codice copiato")} className="btn-ghost-lux flex h-11 w-11 items-center justify-center rounded-full" aria-label="Copia codice"><Copy className="h-4 w-4" /></button>
          </div>

          {/* Pagamento */}
          {payable && (
            <div className="lux-card p-5 sm:p-6" data-testid="order-payment-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-2xl italic text-[#f5f2ee]">Paga {fmtEur(order.total_eur)}</h2>
                {available.length > 1 && (order.status === "in_attesa" || order.status === "scaduto") && (
                  <Select value={p.method} onValueChange={changeMethod} disabled={busy}>
                    <SelectTrigger data-testid="order-change-method" className="h-10 w-auto gap-2 rounded-full border-[rgba(242,215,208,0.2)] bg-[#0b0b0d] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-[rgba(0,175,240,0.2)] bg-[#141418] text-[#f5f2ee]">{available.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`order-method-option-${m.id}`}>{METHOD_META[m.id].short}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              {order.status === "scaduto" && <p className="mt-2 text-sm text-[#ffcc66]">Il tempo e' scaduto: scegli di nuovo il metodo per riattivare l'ordine con il cambio aggiornato.</p>}
              {order.status === "verifica_manuale" && <p className="mt-2 flex items-start gap-2 text-sm text-[#ffcc66]"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> {order.history?.[order.history.length - 1]?.note}. Se hai gia' pagato non serve rifare nulla: controllo io e confermo.</p>}
              {order.status === "pagamento_visto" && <p className="mt-2 flex items-center gap-2 text-sm text-[color:var(--gold-light)]"><Loader2 className="h-4 w-4 animate-spin" /> Transazione vista sulla rete: la conferma arriva di solito entro 2 minuti.</p>}

              {p.method === "sepa" ? (
                <div className="mt-5 space-y-2">
                  <Row label="Intestatario" value={p.holder} mono={false} testId="order-sepa-holder" />
                  <Row label="IBAN" value={p.iban} copy testId="order-sepa-iban" />
                  {p.bank && <Row label="Banca" value={p.bank} mono={false} testId="order-sepa-bank" />}
                  <Row label="Importo" value={`${p.amount} EUR`} copy testId="order-sepa-amount" big />
                  <Row label="Causale (obbligatoria)" value={p.reference} copy testId="order-sepa-reference" big />
                  {p.note && <p className="text-xs text-[#8a847d]">{p.note}</p>}
                  <button data-testid="order-paid-button" onClick={() => setClaimOpen(true)} className="btn-gold mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold uppercase tracking-[0.14em]"><CheckCircle2 className="h-4 w-4" /> Ho fatto il bonifico</button>
                  <div className="flex flex-wrap items-center justify-between gap-2 pt-1"><span className="text-xs text-[#8a847d]">Dubbi su causale o importo?</span><WriteToKimberly item={order.item.name} code={order.code} testId="order-sepa-write" /></div>
                </div>
              ) : (
                <div className="mt-5 space-y-4">
                  {METHOD_META[p.method].wallet && order.status !== "pagamento_visto" && (
                    <>
                      <TonPayButton order={order} headers={headers} onSent={() => setTimeout(() => load(true), 6000)} />
                      <a data-testid="order-open-tonkeeper" href={p.links.tonkeeper} target="_blank" rel="noopener noreferrer" className="btn-ghost-lux flex min-h-[44px] items-center justify-center gap-2 rounded-xl text-xs uppercase tracking-[0.14em]"><ExternalLink className="h-3.5 w-3.5" /> Apri in Tonkeeper</a>
                      <WalletSteps item={order.item.name} code={order.code} />
                      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[#6f6a64]"><span className="h-px flex-1 bg-[rgba(242,215,208,0.12)]" /> oppure a mano <span className="h-px flex-1 bg-[rgba(242,215,208,0.12)]" /></div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Row label={`Importo esatto (${p.currency})`} value={p.amount} copy testId="order-crypto-amount" big />
                    <Row label={`Indirizzo ${p.network === "tron" ? "Tron (TRC20)" : "TON"}`} value={p.address} copy testId="order-crypto-address" />
                    {p.network === "ton" && <Row label={p.memo_required ? "Memo (obbligatorio)" : "Commento (consigliato)"} value={p.comment} copy testId="order-crypto-comment" />}
                  </div>
                  <p className="text-xs text-[#8a847d]">
                    {p.network === "tron" ? "Invia ESATTAMENTE questo importo di USDT sulla rete Tron (TRC20): serve a riconoscere il tuo pagamento. Altre reti = fondi persi." : `Invia esattamente l'importo indicato${p.memo_required ? " con il memo" : ", scrivendo il codice nel commento"}: il pagamento viene riconosciuto da solo in 1-2 minuti.`}
                  </p>
                  <button data-testid="order-toggle-qr" onClick={() => setShowQr(!showQr)} className="btn-ghost-lux flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl text-xs uppercase tracking-[0.14em]"><QrCode className="h-4 w-4" /> {showQr ? "Nascondi QR" : "Mostra QR"}</button>
                  {showQr && <QRBox value={p.links.qr || p.address} />}
                  <div className="grid grid-cols-2 gap-2">
                    <button data-testid="order-check-button" onClick={manualCheck} disabled={busy} className="btn-ghost-lux flex min-h-[44px] items-center justify-center gap-2 rounded-xl text-xs uppercase tracking-[0.14em] disabled:opacity-60">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />} Controlla ora</button>
                    <button data-testid="order-paid-button" onClick={() => setClaimOpen(true)} className="btn-ghost-lux flex min-h-[44px] items-center justify-center gap-2 rounded-xl text-xs uppercase tracking-[0.14em]"><CheckCircle2 className="h-3.5 w-3.5" /> Ho pagato</button>
                  </div>
                  {!METHOD_META[p.method].wallet && <div className="flex flex-wrap items-center justify-between gap-2 pt-1"><span className="text-xs text-[#8a847d]">Ti serve una mano? Ti do io l'indirizzo.</span><WriteToKimberly item={order.item.name} code={order.code} testId="order-crypto-write" /></div>}
                </div>
              )}
            </div>
          )}

          {/* Consegna */}
          {(order.status === "confermato" || order.status === "consegnato") && (
            <div className="card-special rounded-[22px] p-5 sm:p-6" data-testid="order-delivery-area">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[color:var(--rose)]"><Gift className="h-4 w-4" /> Consegna</div>
              {order.delivery_message ? (
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-[#f5f2ee]" data-testid="order-delivery-message">{linkify(order.delivery_message)}</p>
              ) : (
                <p className="mt-3 text-sm text-[#e9e4de]" data-testid="order-delivery-pending">{config?.texts?.thanks || "Pagamento confermato. Sto preparando tutto: il contenuto o il link privato comparira' qui."}</p>
              )}
              <p className="mt-3 text-xs text-[#8a847d]">{order.item.delivery}</p>
            </div>
          )}
          {order.status === "rifiutato" && (
            <div className="lux-card border-[rgba(255,77,77,0.35)] p-5" data-testid="order-rejected">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#ff8a8a]">Ordine rifiutato</div>
              <p className="mt-2 text-sm text-[#f5f2ee]">{order.reject_reason}</p>
            </div>
          )}
        </div>

        <aside className="space-y-5">
          <div className="lux-card p-5">
            {order.status === "in_attesa" && (
              <div className="mb-5" data-testid="order-countdown">
                <div className="flex items-center justify-between text-xs text-[#b9b2aa]"><span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[color:var(--rose)]" /> Prezzo bloccato ancora</span><span className="font-mono text-[#f5f2ee]">{cd.text}</span></div>
                <Progress value={cd.pct} className="mt-2 h-1.5 bg-[rgba(0,175,240,0.12)]" />
              </div>
            )}
            <OrderTimeline order={order} />
          </div>
          <div className="lux-card p-5" data-testid="order-summary">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[color:var(--blush)]"><Package className="h-3.5 w-3.5" /> Riepilogo</div>
            <ul className="mt-3 space-y-1.5">{order.item.details?.map((d, i) => <li key={i} className="text-sm text-[#e9e4de]">• {d}</li>)}</ul>
            <div className="mt-4 space-y-1 border-t border-[rgba(0,175,240,0.14)] pt-3 text-sm">
              <div className="flex justify-between text-[#b9b2aa]"><span>Metodo</span><span>{METHOD_META[p.method].short}</span></div>
              {order.discount_pct > 0 && <div className="flex justify-between text-[color:var(--rose)]"><span>Sconto</span><span>-{order.discount_pct}%</span></div>}
              <div className="flex justify-between text-[#f5f2ee]"><span>Totale</span><span className="font-display text-xl tabular-nums" data-testid="order-total">{fmtEur(order.total_eur)}</span></div>
              {p.rate_eur && <div className="text-right text-[11px] text-[#8a847d]">1 {p.currency} = {Number(p.rate_eur).toFixed(4)} EUR bloccato</div>}
            </div>
            <Collapsible className="mt-4">
              <CollapsibleTrigger data-testid="order-history-toggle" className="text-[11px] uppercase tracking-[0.2em] text-[#8a847d] hover:text-[#f5f2ee]">Cronologia</CollapsibleTrigger>
              <CollapsibleContent>
                <ul className="mt-2 space-y-1.5 text-xs text-[#b9b2aa]" data-testid="order-history">{order.history?.map((h, i) => <li key={i}>{fmtDateTime(h.at)} — {STATUS_META[h.status]?.short || h.status}{h.note ? `: ${h.note}` : ""}</li>)}</ul>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </aside>
      </div>

      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent className="border-[rgba(0,175,240,0.25)] bg-[#111114] text-[#f5f2ee]" data-testid="order-claim-dialog">
          <DialogTitle className="font-display text-2xl italic">{p.method === "sepa" ? "Ho fatto il bonifico" : "Ho pagato"}</DialogTitle>
          <DialogDescription className="text-sm text-[#b9b2aa]">{p.method === "sepa" ? "Facoltativo: incolla il riferimento (CRO/TRN) per velocizzare la verifica." : "Di solito non serve: il pagamento viene riconosciuto da solo. Se vuoi, incolla il TXID per aiutarmi a trovarlo."}</DialogDescription>
          {p.method === "sepa" ? (
            <Input data-testid="order-claim-reference" value={claim.reference} onChange={(e) => setClaim({ ...claim, reference: e.target.value })} placeholder="Riferimento bonifico" className={field} />
          ) : (
            <Input data-testid="order-claim-txid" value={claim.txid} onChange={(e) => setClaim({ ...claim, txid: e.target.value })} placeholder="TXID / hash transazione" className={field} />
          )}
          <button data-testid="order-claim-submit" onClick={submitClaim} disabled={busy} className="btn-gold flex min-h-[48px] items-center justify-center gap-2 rounded-xl text-sm font-semibold uppercase tracking-[0.14em] disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Invia</button>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default function Order() {
  return (
    <Shell testId="order-shell" footer={false}>
      <TonConnectUIProvider manifestUrl={MANIFEST} uiPreferences={{ theme: THEME.DARK }}>
        <OrderInner />
      </TonConnectUIProvider>
    </Shell>
  );
}
